import React from 'react';
import { Crown, Shield, Award, User, Star, GraduationCap } from 'lucide-react';

/**
 * UPIMBadge - Componente de distintivo visual para roles UPIM
 * Muestra el badge correspondiente al tipo de miembro/rol
 */

const BADGE_CONFIG = {
  director_nacional: {
    label: 'Director Nacional UPIM',
    shortLabel: 'Dir. Nacional',
    icon: Crown,
    bgGradient: 'bg-gradient-to-r from-amber-500 to-yellow-400',
    textColor: 'text-amber-900',
    borderColor: 'border-amber-400',
    iconColor: 'text-amber-900'
  },
  director_regional: {
    label: 'Director Regional UPIM',
    shortLabel: 'Dir. Regional',
    icon: Crown,
    bgGradient: 'bg-gradient-to-r from-blue-600 to-blue-500',
    textColor: 'text-white',
    borderColor: 'border-blue-400',
    iconColor: 'text-white'
  },
  consejo_regional: {
    label: 'Consejo Regional UPIM',
    shortLabel: 'Consejo',
    icon: Shield,
    bgGradient: 'bg-gradient-to-r from-indigo-600 to-indigo-500',
    textColor: 'text-white',
    borderColor: 'border-indigo-400',
    iconColor: 'text-white'
  },
  proveedor_certificado: {
    label: 'Proveedor Certificado UPIM',
    shortLabel: 'Proveedor Cert.',
    icon: Award,
    bgGradient: 'bg-gradient-to-r from-purple-600 to-purple-500',
    textColor: 'text-white',
    borderColor: 'border-purple-400',
    iconColor: 'text-white'
  },
  socio_afiliado: {
    label: 'Socio Afiliado UPIM',
    shortLabel: 'Socio',
    icon: Star,
    bgGradient: 'bg-gradient-to-r from-emerald-600 to-emerald-500',
    textColor: 'text-white',
    borderColor: 'border-emerald-400',
    iconColor: 'text-white'
  },
  socio_en_formacion: {
    label: 'Socio en Formación',
    shortLabel: 'En Formación',
    icon: GraduationCap,
    bgGradient: 'bg-gradient-to-r from-gray-500 to-gray-400',
    textColor: 'text-white',
    borderColor: 'border-gray-400',
    iconColor: 'text-white'
  }
};

const UPIMBadge = ({ 
  tipo = 'socio_en_formacion', 
  size = 'md', // 'sm', 'md', 'lg', 'xl'
  variant = 'full', // 'full', 'compact', 'icon-only', 'inline'
  showIcon = true,
  className = '',
  areaName = null, // Para mostrar el área si es director
  estadoName = null // Para mostrar el estado si es regional
}) => {
  const config = BADGE_CONFIG[tipo] || BADGE_CONFIG.socio_en_formacion;
  const IconComponent = config.icon;

  // Tamaños
  const sizeClasses = {
    sm: {
      container: 'px-2 py-1',
      icon: 'w-3 h-3',
      text: 'text-xs',
      iconOnly: 'w-6 h-6'
    },
    md: {
      container: 'px-3 py-1.5',
      icon: 'w-4 h-4',
      text: 'text-sm',
      iconOnly: 'w-8 h-8'
    },
    lg: {
      container: 'px-4 py-2',
      icon: 'w-5 h-5',
      text: 'text-base',
      iconOnly: 'w-10 h-10'
    },
    xl: {
      container: 'px-5 py-2.5',
      icon: 'w-6 h-6',
      text: 'text-lg',
      iconOnly: 'w-12 h-12'
    }
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  // Variante: Solo icono
  if (variant === 'icon-only') {
    return (
      <div 
        className={`${config.bgGradient} ${sizes.iconOnly} rounded-lg flex items-center justify-center shadow-md ${className}`}
        title={config.label}
        data-testid={`upim-badge-${tipo}`}
      >
        <IconComponent className={`${sizes.icon} ${config.iconColor}`} />
      </div>
    );
  }

  // Variante: Inline (para usar dentro de texto)
  if (variant === 'inline') {
    return (
      <span 
        className={`inline-flex items-center gap-1 ${config.bgGradient} ${config.textColor} ${sizes.container} rounded-full font-medium ${sizes.text} ${className}`}
        data-testid={`upim-badge-${tipo}`}
      >
        {showIcon && <IconComponent className={sizes.icon} />}
        <span>{config.shortLabel}</span>
      </span>
    );
  }

  // Variante: Compacta
  if (variant === 'compact') {
    return (
      <div 
        className={`inline-flex items-center gap-1.5 ${config.bgGradient} ${config.textColor} ${sizes.container} rounded-lg font-semibold shadow-sm ${className}`}
        data-testid={`upim-badge-${tipo}`}
      >
        {showIcon && <IconComponent className={sizes.icon} />}
        <span className={sizes.text}>{config.shortLabel}</span>
      </div>
    );
  }

  // Variante: Completa (default)
  return (
    <div 
      className={`inline-flex flex-col items-center ${className}`}
      data-testid={`upim-badge-${tipo}`}
    >
      <div className={`${config.bgGradient} ${config.textColor} ${sizes.container} rounded-xl font-semibold shadow-md flex items-center gap-2`}>
        {showIcon && <IconComponent className={sizes.icon} />}
        <div className="flex flex-col">
          <span className={sizes.text}>{config.label}</span>
          {(areaName || estadoName) && (
            <span className={`${sizes.text} opacity-80 font-normal`}>
              {areaName && areaName}
              {areaName && estadoName && ' • '}
              {estadoName && estadoName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente para mostrar múltiples badges
export const UPIMBadgeList = ({ distintivos = [], size = 'sm', className = '' }) => {
  if (!distintivos || distintivos.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {distintivos.map((distintivo, index) => (
        <UPIMBadge 
          key={distintivo.id || index}
          tipo={distintivo.tipo}
          size={size}
          variant="compact"
        />
      ))}
    </div>
  );
};

// Componente para mostrar badge en perfil
export const UPIMProfileBadge = ({ 
  tipo, 
  nombre, 
  titulo, 
  areaName, 
  estadoName,
  className = '' 
}) => {
  const config = BADGE_CONFIG[tipo] || BADGE_CONFIG.socio_en_formacion;
  const IconComponent = config.icon;

  return (
    <div className={`rounded-2xl overflow-hidden shadow-lg ${className}`} data-testid="upim-profile-badge">
      {/* Header con gradiente */}
      <div className={`${config.bgGradient} px-6 py-4`}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <IconComponent className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          <div className={config.textColor}>
            <p className="font-bold text-lg">{config.label}</p>
            {areaName && <p className="text-sm opacity-90">{areaName}</p>}
            {estadoName && <p className="text-sm opacity-90">{estadoName}</p>}
          </div>
        </div>
      </div>
      
      {/* Body */}
      <div className="bg-white px-6 py-4">
        <p className="font-semibold text-gray-900 text-lg">{nombre}</p>
        {titulo && <p className="text-gray-600">{titulo}</p>}
        <div className="mt-3 flex items-center gap-2">
          <img 
            src="/assets/upim-logo.svg"
            alt="UPIM"
            className="h-6"
          />
          <span className="text-xs text-gray-500">Unión de Profesionales Inmobiliarios de México</span>
        </div>
      </div>
    </div>
  );
};

export default UPIMBadge;
